/**
 * Brings Horizon's quick add modal in line with the old theme's quick view, for the things CSS
 * cannot reach: a "View Full Product" link, the wording of the variant option legend, and the
 * member price when the modal is opened from the perks page.
 *
 * Both depend on content that `quick-add.js` fetches into the dialog, so they are applied by
 * watching that content rather than by forking Horizon's own script. The content is replaced
 * wholesale each time the modal opens, so the observer stays for the life of the page.
 *
 * The two breakpoints fetch different fragments: desktop wraps the details in `.product-details`
 * and titles it `.view-product-title`, mobile has no wrapper and uses `.product-header`. Both are
 * handled by looking for either, and by falling back to the content element as the container.
 */
import { formatMoney } from '@theme/money-formatting';

const CONTENT_ID = 'quick-add-modal-content';
const LINK_CLASS = 'eq-quick-add-modal__view-link';
const TITLE_LINK = '.view-product-title a, .product-header a';

const MEMBER_HOST = '[data-eq-member-discount]';
const MEMBER_SAVE_CLASS = 'eq-quick-add-modal__member-save';
const PRICE_CONTAINER = 'product-price [ref="priceContainer"]';
const SELECTED_VARIANT = '.variant-picker__form script[type="application/json"]';

/**
 * Member pricing of the card whose quick add opened the modal, or null for any other card.
 *
 * @type {{ discount: number, format: string, currency: string } | null}
 */
let memberPricing = null;

/** Placeholder the Liquid leaves in `data-eq-option-legend` for the option name. */
const OPTION_TOKEN = '[option]';

/** @param {HTMLElement} content */
const linkLabel = (content) => content.dataset.eqViewProductLabel ?? '';

/**
 * Live ends the legend with a colon. The modal renders the PDP's own picker, whose legend already
 * reads "Select <option>", so the template adds only the colon — prefixing "Select " here too is
 * what produced "Select Select Size:". The label comes from the markup rather than being
 * hard-coded, so a product varying on anything else reads correctly.
 *
 * @param {HTMLElement} content
 * @param {string} optionName
 */
const legendLabel = (content, optionName) =>
  (content.dataset.eqOptionLegend ?? '').replace(OPTION_TOKEN, optionName);

/**
 * Supplies the legend the default product template never renders.
 *
 * `snippets/eq-variant-main-picker.liquid` opens its fieldset straight onto the option tiles, so a
 * product on that template reaches the modal with nothing for `renderLegends` to reword and the
 * options go unlabelled. The other templates' pickers render "Select <option>" themselves, so the
 * same wording is built here from the option name the radios already carry, leaving the product
 * page's own markup alone — the legend exists in the modal only.
 *
 * @param {HTMLElement} content
 */
const addMissingLegends = (content) => {
  for (const fieldset of content.querySelectorAll('.variant-option')) {
    if (fieldset.querySelector('legend')) continue;

    const optionName = /** @type {HTMLElement | null} */ (fieldset.querySelector('[data-option-name]'))?.dataset
      .optionName;
    if (!optionName) continue;

    const label = (content.dataset.eqOptionSelectLabel ?? '').replace(OPTION_TOKEN, optionName);
    if (!label) continue;

    const legend = document.createElement('legend');
    legend.textContent = label;
    fieldset.prepend(legend);
  }
};

/**
 * Adds the link under the buy buttons.
 *
 * Every write is guarded by a comparison. The observer watches the subtree this runs inside, so an
 * unconditional write would re-trigger it forever.
 *
 * @param {HTMLElement} content
 */
const renderViewLink = (content) => {
  const source = content.querySelector(TITLE_LINK);

  // Nothing fetched yet, or a fragment without a product link — leave the modal alone.
  if (!(source instanceof HTMLAnchorElement)) return;

  const container = content.querySelector('.product-details') ?? content;

  let wrapper = content.querySelector(`.${LINK_CLASS}`);

  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = LINK_CLASS;
    wrapper.appendChild(document.createElement('a'));
  }

  const link = wrapper.firstElementChild;
  if (!(link instanceof HTMLAnchorElement)) return;

  const label = linkLabel(content);
  if (!label) return;

  const href = source.getAttribute('href') ?? '';
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  if (link.textContent !== label) link.textContent = label;

  // Last child, so it sits under the buy buttons as it did on the old modal.
  if (container.lastElementChild !== wrapper) container.appendChild(wrapper);
};

/**
 * Rewrites each option legend to the old theme's wording.
 *
 * The original name is stashed on the element the first time round, so re-running never compounds
 * the prefix onto an already-rewritten legend. A legend Horizon has re-rendered arrives without
 * the attribute and is simply read afresh.
 *
 * @param {HTMLElement} content
 */
const renderLegends = (content) => {
  addMissingLegends(content);

  for (const legend of content.querySelectorAll('.variant-option legend')) {
    if (!(legend instanceof HTMLElement)) continue;

    // A swatch legend is the option name followed by a `.variant-option__swatch-value` span that
    // Horizon keeps updated. Only the leading text node is read and rewritten, so the span
    // survives and the name is never taken with the selected value stuck to it.
    const nameNode = [...legend.childNodes].find(
      (node) => node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()
    );
    if (!nameNode) continue;

    const optionName = legend.dataset.eqOptionName ?? nameNode.nodeValue?.trim();
    if (!optionName) continue;

    if (!legend.dataset.eqOptionName) legend.dataset.eqOptionName = optionName;

    const label = legendLabel(content, optionName);
    if (label && nameNode.nodeValue !== label) nameNode.nodeValue = label;
  }
};

/**
 * @param {HTMLElement} content
 * @returns {{ id: number, price: number, compare_at_price: number | null } | null}
 */
const selectedVariant = (content) => {
  const script = content.querySelector(SELECTED_VARIANT);
  if (!script?.textContent) return null;

  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
};

/**
 * @param {string} className
 * @param {string} text
 */
const priceItem = (className, text) => {
  const item = document.createElement('span');
  item.className = className;
  item.textContent = text;
  return item;
};

/**
 * Shows the perks member price, as the old quick view did: the regular (or compare-at) price
 * struck through, the discounted price, and a "Save" line. Display only — the discount itself is
 * applied at checkout.
 *
 * Horizon swaps the price container on every variant change, so the container is stamped with the
 * variant it was written for and rewritten only when that changes.
 *
 * @param {HTMLElement} content
 */
const renderMemberPrice = (content) => {
  const save = content.querySelector(`.${MEMBER_SAVE_CLASS}`);

  if (!memberPricing) {
    save?.remove();
    return;
  }

  const container = content.querySelector(PRICE_CONTAINER);
  const variant = selectedVariant(content);
  if (!(container instanceof HTMLElement) || !variant) return;

  const { discount, format, currency } = memberPricing;
  const stamp = `${variant.id}:${discount}`;
  if (container.dataset.eqMemberPrice === stamp && save) return;

  const finalPrice = variant.price - Math.round((variant.price * discount) / 100);
  const hasCompare = (variant.compare_at_price ?? 0) > variant.price;
  const comparePrice = hasCompare ? /** @type {number} */ (variant.compare_at_price) : variant.price;
  const percentage = hasCompare ? ((comparePrice - finalPrice) / comparePrice) * 100 : discount;

  container.dataset.eqMemberPrice = stamp;
  container.replaceChildren(
    priceItem('price-item__group compare-at-price', formatMoney(comparePrice, format, currency)),
    document.createTextNode(' '),
    priceItem('price-item__group price eq-quick-add-modal__member-price', formatMoney(finalPrice, format, currency))
  );

  const line = save ?? document.createElement('p');
  line.className = MEMBER_SAVE_CLASS;
  // Floored to agree with the card's "% OFF" badge, which Liquid's integer division truncates.
  line.textContent = (content.dataset.eqMemberSaveLabel ?? '').replace('[percent]', String(Math.floor(percentage)));

  const host = container.closest('product-price');
  if (host && host.nextElementSibling !== line) host.after(line);
};

/** @param {HTMLElement} content */
const render = (content) => {
  renderViewLink(content);
  renderLegends(content);
  renderMemberPrice(content);
};

/**
 * Opts the modal into the mobile cart notification, which `eq-cart-notification.js` finds by
 * walking up from the add's source element. The product has to be named here rather than in the
 * Liquid because the dialog is one element reused by every card.
 *
 * @param {Element} button
 */
const markNotificationSource = (button) => {
  const content = document.getElementById(CONTENT_ID);
  if (!(content instanceof HTMLElement)) return;

  const component = button.closest('quick-add-component');
  // The card's own image, not merely its first — a badge or the hover image landing earlier in the
  // markup would otherwise end up in the toast.
  const image = button.closest('.eq-product-card')?.querySelector('.eq-product-card__image');
  const source = image instanceof HTMLImageElement ? image.currentSrc || image.src : '';

  content.dataset.eqCartMobileNotification = 'true';
  content.dataset.eqCartNotificationName =
    component instanceof HTMLElement ? component.dataset.productTitle ?? '' : '';

  if (source) content.dataset.eqCartNotificationImage = source;
  else delete content.dataset.eqCartNotificationImage;
};

/**
 * Records whether a quick add was opened from a perks card. Captured before `quick-add.js` handles
 * the click, so the pricing is known by the time the fetched content lands.
 *
 * @param {MouseEvent} event
 */
const trackOpener = (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('.quick-add__button');
  if (!button) return;

  markNotificationSource(button);

  const host = button.closest(MEMBER_HOST);
  const discount = Number(host instanceof HTMLElement ? host.dataset.eqMemberDiscount : 0);

  memberPricing =
    host instanceof HTMLElement && discount > 0
      ? { discount, format: host.dataset.moneyFormat ?? '{{amount}}', currency: host.dataset.currency ?? '' }
      : null;
};

const start = () => {
  const content = document.getElementById(CONTENT_ID);
  if (!content) return;

  document.addEventListener('click', trackOpener, { capture: true });
  new MutationObserver(() => render(content)).observe(content, { childList: true, subtree: true });

  render(content);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
